import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import {
  COMPETENCY_PERMS as P,
  useCompetencyAction,
  useCompetencyCan,
  useCompetencyOverview,
  useTrainingCourses,
  useTrainingSchedule,
} from '@/hooks/useCompetency';
import { competencyService, type TrainingPlanItem } from '@/services/competency.service';
import departmentService from '@/services/department.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { MONTH_LABELS, PLAN_SOURCES, STATUS_COLOR, employeeLabel } from './competencyDisplay';

const { Text } = Typography;

const TrainingSchedulePage: React.FC = () => {
  const canManage = useCompetencyCan(P.trainingManage);
  const [year, setYear] = useState(new Date().getFullYear());
  const [departmentId, setDepartmentId] = useState<string>();
  const [courseId, setCourseId] = useState<string>();
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm();
  const target = Form.useWatch('target', form);

  const { data, isLoading } = useTrainingSchedule(year, { department_id: departmentId, course_id: courseId });
  const { data: courses = [] } = useTrainingCourses({ active: '1' });
  const { data: employees = [] } = useCompetencyOverview(undefined, canManage);
  const { data: departments = [] } = useQuery({ queryKey: ['departments', 'selection'], queryFn: () => departmentService.getForSelection(), staleTime: 300_000 });

  const add = useCompetencyAction((v: Record<string, unknown>) => competencyService.addScheduleItems(v), 'Schedule updated');
  const update = useCompetencyAction((v: { id: string; data: Record<string, unknown> }) => competencyService.updateScheduleItem(v.id, v.data));
  const remove = useCompetencyAction((id: string) => competencyService.deleteScheduleItem(id), 'Line deleted');

  const toggleMonth = (item: TrainingPlanItem, month: number) => {
    const months = item.planned_months.includes(month) ? item.planned_months.filter((m) => m !== month) : [...item.planned_months, month];
    update.mutate({ id: item.id, data: { planned_months: months } });
  };

  const monthColumns: ColumnsType<TrainingPlanItem> = MONTH_LABELS.map((label, i) => ({
    title: label,
    key: `m${i + 1}`,
    width: 52,
    align: 'center' as const,
    render: (_: unknown, r: TrainingPlanItem) => {
      const m = i + 1;
      const planned = r.planned_months.includes(m);
      const achieved = r.achieved_months.includes(m);
      const editable = canManage && r.status === 'Planned';
      return (
        <div
          onClick={editable ? () => toggleMonth(r, m) : undefined}
          style={{ cursor: editable ? 'pointer' : 'default', minHeight: 36, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
        >
          {planned ? <Tag color="blue" style={{ margin: 0 }}>P</Tag> : <span style={{ color: '#d9d9d9' }}>.</span>}
          {achieved ? <Tag color="green" style={{ margin: 0 }}>A</Tag> : null}
        </div>
      );
    },
  }));

  const columns: ColumnsType<TrainingPlanItem> = [
    {
      title: 'Training',
      dataIndex: 'course_title',
      fixed: 'left',
      width: 220,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.course_code}
            {r.source_type !== 'TNA' ? ` | ${r.source_type}${r.source_ref ? ` ${r.source_ref}` : ''}` : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Participants',
      width: 200,
      render: (_, r) => (r.employee_id ? employeeLabel({ employee_number: r.employee_number, full_name: r.employee_name }) : <Tag>Dept: {r.department_name}</Tag>),
    },
    { title: 'Trainer', dataIndex: 'trainer', width: 130 },
    { title: 'Frequency', dataIndex: 'frequency', width: 110 },
    ...monthColumns,
    { title: 'Status', dataIndex: 'status', width: 100, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    {
      title: '',
      width: 80,
      fixed: 'right',
      render: (_, r) =>
        canManage ? (
          <Space size={4}>
            {r.status === 'Planned' && (
              <Tooltip title="Cancel">
                <Button size="small" icon={<StopOutlined />} onClick={() => update.mutate({ id: r.id, data: { status: 'Cancelled' } })} />
              </Tooltip>
            )}
            <Popconfirm title="Delete this line?" onConfirm={() => remove.mutate(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title={`Annual training schedule ${year}`}
        extra={
          <Space wrap>
            <ReportDocumentActions
              reportKey="competency-training-schedule"
              params={{ year: String(year), department_id: departmentId ?? '', course_id: courseId ?? '' }}
              sendPermissions={[P.trainingManage]}
            />
            {canManage && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  form.resetFields();
                  form.setFieldsValue({ target: 'employees', source_type: 'TNA', planned_months: [] });
                  setAdding(true);
                }}
              >
                Add training
              </Button>
            )}
          </Space>
        }
      />
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <InputNumber value={year} min={2000} max={2100} onChange={(v) => v && setYear(Number(v))} addonBefore="Year" style={{ width: 150 }} />
          <Select
            allowClear
            placeholder="Department"
            style={{ width: 220 }}
            value={departmentId}
            onChange={setDepartmentId}
            showSearch
            optionFilterProp="label"
            options={departments.map((d) => ({ value: d.id, label: d.department_name }))}
          />
          <Select
            allowClear
            placeholder="Course"
            style={{ width: 260 }}
            value={courseId}
            onChange={setCourseId}
            showSearch
            optionFilterProp="label"
            options={courses.map((c) => ({ value: c.id, label: `${c.code} - ${c.title}` }))}
          />
          {data && (
            <Text>
              Planned {data.summary.planned}, achieved {data.summary.achieved}
              {data.summary.achievement_percent !== null ? ` (${data.summary.achievement_percent}%)` : ''}
            </Text>
          )}
          {data?.plan?.updated_at && (
            <Text type="secondary">
              Last updated {formatDate(data.plan.updated_at)} {data.plan.updated_by_name ? `by ${data.plan.updated_by_name}` : ''}
            </Text>
          )}
        </Space>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          P = planned, A = achieved (from completed attendance). {canManage ? 'Click a month to plan or unplan it.' : ''}
        </Text>
        <Table
          rowKey="id"
          size="small"
          bordered
          loading={isLoading}
          dataSource={data?.items ?? []}
          columns={columns}
          scroll={{ x: 1500 }}
          pagination={{ defaultPageSize: 50, showSizeChanger: true }}
        />
      </Card>

      <Modal
        title="Add training to the schedule"
        open={adding}
        width={720}
        onCancel={() => setAdding(false)}
        onOk={() => form.submit()}
        confirmLoading={add.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (v) => {
            await add.mutateAsync({
              year,
              course_id: v.course_id,
              employee_ids: v.target === 'employees' ? v.employee_ids : undefined,
              department_id: v.target === 'department' ? v.department_id : undefined,
              trainer: v.trainer,
              frequency: v.frequency,
              planned_months: v.planned_months,
              source_type: v.source_type,
              notes: v.notes,
            });
            setAdding(false);
          }}
        >
          <Form.Item name="course_id" label="Training course" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={courses.map((c) => ({ value: c.id, label: `${c.code} - ${c.title}` }))}
              onChange={(cid) => {
                const c = courses.find((x) => x.id === cid);
                form.setFieldsValue({ trainer: c?.default_trainer ?? undefined, frequency: c?.frequency ?? undefined });
              }}
            />
          </Form.Item>
          <Form.Item name="target" label="For">
            <Radio.Group
              options={[
                { value: 'employees', label: 'Selected employees (one line each)' },
                { value: 'department', label: 'A whole department (one line)' },
              ]}
            />
          </Form.Item>
          {target === 'department' ? (
            <Form.Item name="department_id" label="Department" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" options={departments.map((d) => ({ value: d.id, label: d.department_name }))} />
            </Form.Item>
          ) : (
            <Form.Item name="employee_ids" label="Employees" rules={[{ required: true, type: 'array', min: 1 }]}>
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                options={employees.map((e) => ({ value: e.id, label: `${employeeLabel(e)}${e.department_name ? ` (${e.department_name})` : ''}` }))}
              />
            </Form.Item>
          )}
          <Form.Item name="planned_months" label="Planned months" rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one month' }]}>
            <Checkbox.Group options={MONTH_LABELS.map((m, i) => ({ value: i + 1, label: m }))} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="trainer" label="Trainer">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="frequency" label="Frequency">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="source_type" label="Reason">
                <Select options={PLAN_SOURCES.map((s) => ({ value: s, label: s === 'TNA' ? 'Training need (TNA)' : s }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TrainingSchedulePage;
